import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Star, FlaskConical, PartyPopper } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import ConsentCheckbox from "./ConsentCheckbox";

function Stars({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" data-testid={`beta-review-star-${n}`} onMouseEnter={() => setHover(n)} onClick={() => onChange(n)}
          className="p-0.5 transition-transform hover:scale-110" aria-label={`${n} stelle`}>
          <Star style={{ width: 30, height: 30 }} className={(hover || value) >= n ? "text-amber-400 fill-amber-400" : "text-slate-300"} />
        </button>
      ))}
    </div>
  );
}

function BetaReviewModal({ beta, onDone }) {
  const [rating, setRating] = useState(0);
  const [role, setRole] = useState("");
  const [comment, setComment] = useState("");
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!rating) return toast.error("Seleziona da 1 a 5 stelle");
    if (comment.trim().length < 10) return toast.error("Scrivi almeno qualche parola sulla tua esperienza (minimo 10 caratteri)");
    setSaving(true);
    try {
      const { data } = await api.post("/reviews", { rating, role, comment, consent_public: consent });
      toast.success(data.status === "simulated" ? "Recensione simulata: non è stata salvata né pubblicata"
        : data.status === "approved" ? "Grazie! La tua recensione è stata pubblicata" : "Grazie! La tua recensione è stata salvata");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setSaving(false);
    }
  };
  return (
    <div data-testid="beta-review-modal" role="dialog" aria-modal="true" className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-lg p-6 sm:p-8 max-h-[92vh] overflow-y-auto">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-3 py-1 text-xs font-bold">
          <FlaskConical style={{ width: 13, height: 13 }} /> Fase Beta completata · {beta.quotes_created}/{beta.limit} preventivi
        </span>
        <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 mt-4">Recensione della Fase Beta</h2>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          Hai usato MyQuoteFlow Pro gratis per {beta.limit} preventivi. Per "pagare" la Fase Beta lascia la tua recensione: verrà pubblicata sulla Landing Page.
          Dopo la pubblicazione il piano Beta Tester termina e potrai scegliere il piano Fondatore, Mensile o Annuale.
        </p>
        <div className="mt-5"><Stars value={rating} onChange={setRating} /></div>
        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mt-5">Professione / attività</label>
        <Input data-testid="beta-review-role" value={role} maxLength={80} onChange={(e) => setRole(e.target.value)} placeholder="Es. Web designer freelance" className="mt-1.5" />
        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mt-4">La tua esperienza</label>
        <Textarea data-testid="beta-review-comment" value={comment} maxLength={1000} rows={4} onChange={(e) => setComment(e.target.value)} placeholder="Cosa ti è piaciuto? Cosa miglioreresti?" className="mt-1.5" />
        <ConsentCheckbox id="beta-review-consent" checked={consent} onChange={setConsent} />
        <Button data-testid="beta-review-submit" onClick={submit} disabled={saving} className="w-full mt-4 h-11 font-semibold">
          {saving ? "Invio…" : "Invia recensione"}
        </Button>
        <p className="text-[11px] text-slate-400 mt-3 text-center">I tuoi preventivi già creati restano sempre consultabili.</p>
      </div>
    </div>
  );
}

export default function BetaGate() {
  const location = useLocation();
  const navigate = useNavigate();
  const [beta, setBeta] = useState(null);
  const [isPro, setIsPro] = useState(false);

  const load = useCallback(() => {
    api.get("/billing/status").then(({ data }) => { setBeta(data.beta); setIsPro(data.is_pro); }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [location.pathname, load]);
  useEffect(() => {
    window.addEventListener("qf:billing-refresh", load);
    return () => window.removeEventListener("qf:billing-refresh", load);
  }, [load]);

  const ended = beta?.ended && !isPro;
  const recentlyEnded = ended && beta?.ended_at && Date.now() - new Date(beta.ended_at).getTime() < 30 * 864e5;

  if (!beta) return null;
  if (beta.review_required) {
    return <BetaReviewModal beta={beta} onDone={() => { setBeta({ ...beta, review_required: false, ended: true, ended_at: new Date().toISOString() }); setIsPro(false); navigate("/dashboard", { replace: true }); }} />;
  }
  if (recentlyEnded) {
    return (
      <div data-testid="beta-ended-banner" className="mb-5 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 flex items-center gap-3">
        <PartyPopper style={{ width: 16, height: 16 }} className="text-violet-600 shrink-0" />
        <p className="text-sm text-violet-900 flex-1 min-w-0">Grazie per aver completato il test! Ora sei sul piano Starter gratuito. L'8 febbraio riceverai l'accesso riservato all'Offerta Fondatore.</p>
      </div>
    );
  }
  if (beta.active) {
    return (
      <div data-testid="beta-active-pill" className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-3 py-1 text-xs font-semibold">
        <FlaskConical style={{ width: 13, height: 13 }} /> Beta Tester · {beta.quotes_created}/{beta.limit} preventivi gratuiti usati
      </div>
    );
  }
  return null;
}
